import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { clientService, type Client } from '../../services/clientService';

interface ManualEmailHistoryEntry {
  id: string;
  source: string;
  status: string;
  to: string;
  subject: string;
  messageId: string;
  sentAt: string;
  error: string;
}

const ClientEmailManager = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [history, setHistory] = useState<ManualEmailHistoryEntry[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [form, setForm] = useState({
    to: '',
    subject: '',
    htmlBody: '',
  });

  const load = async () => {
    const [clientsData, historyResponse] = await Promise.all([
      clientService.getClients(true),
      fetch('/api/admin/send-client-email', { credentials: 'include', cache: 'no-store' }).then((response) =>
        response.json().catch(() => ({}))
      ),
    ]);
    setClients(clientsData);
    setHistory(Array.isArray(historyResponse?.history) ? historyResponse.history : []);
  };

  useEffect(() => {
    load().catch((error) => {
      toast.error(error instanceof Error ? error.message : 'Erreur messagerie');
    });
  }, []);

  const sendEmail = async () => {
    if (!form.to.trim() || !form.subject.trim() || !form.htmlBody.trim()) {
      toast.error('To, sujet et message sont requis');
      return;
    }
    setIsSending(true);
    try {
      const response = await fetch('/api/admin/send-client-email', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.error || 'Envoi email impossible');
      }
      toast.success('Email envoye au client');
      setForm((current) => ({ ...current, subject: '', htmlBody: '' }));
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erreur envoi');
      await load();
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="space-y-6 p-6">
      <section className="rounded-2xl border border-gray-200 bg-white p-5">
        <h2 className="text-2xl font-bold text-gray-900">Messagerie client pro</h2>
        <p className="mt-2 text-sm text-gray-600">
          Ecris et envoie un email directement depuis ton admin, avec statut d envoi trace.
        </p>
        <div className="mt-4 grid gap-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Client</label>
              <select
                value=""
                onChange={(event) => {
                  const selected = clients.find((client) => client.id === event.target.value);
                  if (selected) {
                    setForm((current) => ({ ...current, to: selected.email }));
                  }
                  event.currentTarget.value = '';
                }}
                className="w-full rounded-lg border border-gray-300 px-3 py-2"
              >
                <option value="">Choisir un client</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name} · {client.email}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Email destinataire</label>
              <input
                type="email"
                value={form.to}
                onChange={(event) => setForm((current) => ({ ...current, to: event.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2"
              />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Sujet</label>
            <input
              value={form.subject}
              onChange={(event) => setForm((current) => ({ ...current, subject: event.target.value }))}
              className="w-full rounded-lg border border-gray-300 px-3 py-2"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Message (HTML simple)</label>
            <textarea
              rows={8}
              value={form.htmlBody}
              onChange={(event) => setForm((current) => ({ ...current, htmlBody: event.target.value }))}
              className="w-full rounded-lg border border-gray-300 px-3 py-2"
              placeholder="<p>Bonjour ...</p>"
            />
          </div>
          <div>
            <button
              type="button"
              onClick={sendEmail}
              disabled={isSending}
              className="rounded-lg bg-neon-orange px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
            >
              {isSending ? 'Envoi...' : 'Envoyer email client'}
            </button>
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-gray-200 bg-white p-5">
        <h3 className="text-lg font-semibold text-gray-900">Historique emails manuels</h3>
        <div className="mt-3 space-y-2">
          {history.length === 0 ? (
            <p className="text-sm text-gray-500">Aucun email manuel pour le moment.</p>
          ) : (
            history.map((entry) => (
              <div key={entry.id} className="rounded-lg border border-gray-200 px-3 py-2 text-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="font-medium text-gray-900">{entry.subject}</div>
                  <span
                    className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                      entry.status === 'sent'
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-red-100 text-red-700'
                    }`}
                  >
                    {entry.status}
                  </span>
                </div>
                <div className="text-gray-600">To: {entry.to}</div>
                <div className="text-xs text-gray-500">
                  {entry.sentAt ? new Date(entry.sentAt).toLocaleString('fr-FR') : 'N/A'}
                  {entry.messageId ? ` · Message ID: ${entry.messageId}` : ''}
                </div>
                {entry.error && <div className="text-xs text-red-600">{entry.error}</div>}
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
};

export default ClientEmailManager;
